import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getApiBase } from '../api/api';
import { getToken } from '../utils/session';

const inp = 'h-8 px-2 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40';
const thCls = 'text-left px-3 py-2 font-semibold text-sap-textMuted uppercase tracking-wider text-2xs bg-gray-50 border-b border-sap-border';

function OrderPickerDialog({ open, onClose, onSelect }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setSearch('');
      return;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const delay = search.trim() === '' ? 0 : 250;
    const t = setTimeout(() => {
      setLoading(true);
      api.get(`/production-orders/issuable-orders?search=${encodeURIComponent(search)}`)
        .then((d) => {
          let list = Array.isArray(d) ? d : [];
          const lineCnt = (o) => Number(o.component_count) || 0;
          const pid = (o) => Number(o.prod_order_id) || 0;
          const ordNo = (o) => String(o.order_no || '').trim();
          // Same document number can exist on more than one prod_order_id; put the row with more
          // components first so Issue for Production matches the production order you are viewing.
          list = [...list].sort((a, b) => {
            if (ordNo(a) && ordNo(a) === ordNo(b)) {
              const dLines = lineCnt(b) - lineCnt(a);
              if (dLines !== 0) return dLines;
            }
            return pid(b) - pid(a);
          });
          setRows(list);
        })
        .catch(() => setRows([]))
        .finally(() => setLoading(false));
    }, delay);
    return () => clearTimeout(t);
  }, [open, search]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-[min(760px,94vw)] max-h-[70vh] flex flex-col">
        <div className="px-4 py-3 border-b border-sap-border flex items-center justify-between">
          <h3 className="text-sm font-bold text-sap-text">Select Production Order</h3>
          <button onClick={onClose} className="text-sap-textLight hover:text-red-500 text-lg leading-none">&times;</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search order #, item…" autoFocus
            className={`w-full ${inp}`} />
        </div>
        <div className="flex-1 overflow-auto">
          {(() => {
            const ordKey = (o) => String(o.order_no || '').trim().toLowerCase();
            const byKey = new Map();
            rows.forEach((o) => {
              const k = ordKey(o);
              if (!k) return;
              byKey.set(k, (byKey.get(k) || 0) + 1);
            });
            const hasDupDoc = [...byKey.values()].some((n) => n > 1);
            return (
              <>
                {hasDupDoc && (
                  <div className="px-3 py-2 text-2xs text-amber-900 bg-amber-50 border-b border-amber-200">
                    Multiple rows share the same order number. Use <strong>Internal ID</strong> (same number as in your browser URL <span className="whitespace-nowrap">/production-order/<em>id</em></span>) and <strong>Cmp</strong> (component lines) to pick the correct production order.
                  </div>
                )}
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#F0F4F3] sticky top-0">
                      <th className="text-right px-2 py-2 font-semibold text-sap-text w-14">ID</th>
                      <th className="text-center px-2 py-2 font-semibold text-sap-text w-12">Cmp</th>
                      <th className="text-left px-3 py-2 font-semibold text-sap-text">Order #</th>
                      <th className="text-left px-3 py-2 font-semibold text-sap-text">Item No.</th>
                      <th className="text-left px-3 py-2 font-semibold text-sap-text">Description</th>
                      <th className="text-center px-3 py-2 font-semibold text-sap-text">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-sap-textLight italic">Loading…</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan={6} className="px-3 py-6 text-center text-sap-textLight italic">No eligible orders found.</td></tr>
                    ) : rows.map((o, i) => (
                      <tr key={o.prod_order_id}
                        onClick={() => setSelected(o)}
                        onDoubleClick={() => onSelect(o)}
                        className={`cursor-pointer hover:bg-sap-blue/10 ${selected?.prod_order_id === o.prod_order_id ? 'bg-sap-blue/20 ring-1 ring-sap-blue' : i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'}`}>
                        <td className="px-2 py-2 text-right tabular-nums text-sap-textLight">{o.prod_order_id}</td>
                        <td className="px-2 py-2 text-center tabular-nums font-semibold">{Number(o.component_count) || 0}</td>
                        <td className="px-3 py-2 font-semibold text-sap-blue">{o.order_no}</td>
                        <td className="px-3 py-2">{o.item_no}</td>
                        <td className="px-3 py-2 max-w-[200px] truncate" title={o.description}>{o.description}</td>
                        <td className="px-3 py-2 text-center">{o.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            );
          })()}
        </div>
        <div className="px-4 py-2 border-t border-sap-border flex justify-end gap-2">
          <button onClick={() => selected && onSelect(selected)} disabled={!selected}
            className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-40">Choose</button>
          <button onClick={onClose}
            className="px-4 py-1.5 text-xs border border-sap-border rounded hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SelectItemsToCopyDialog({ open, order, onClose, onConfirm }) {
  const [comps, setComps] = useState([]);
  const [checked, setChecked] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setLoading(true);
    setChecked({});
    api.get(`/production-orders/order-components/${order.prod_order_id}`)
      .then(d => {
        const items = (Array.isArray(d) ? d : []);
        setComps(items);
        const all = {};
        items.forEach((_, i) => { all[i] = true; });
        setChecked(all);
      })
      .catch(() => setComps([]))
      .finally(() => setLoading(false));
  }, [open, order]);

  if (!open || !order) return null;

  const toggleAll = (val) => {
    const next = {};
    comps.forEach((_, i) => { next[i] = val; });
    setChecked(next);
  };

  const allChecked = comps.length > 0 && comps.every((_, i) => checked[i]);
  const selectedComps = comps.filter((_, i) => checked[i]);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-[900px] max-h-[75vh] flex flex-col">
        <div className="px-4 py-3 border-b border-sap-border">
          <h3 className="text-sm font-bold text-sap-text">Select Items to Copy</h3>
          <p className="text-xs text-sap-textLight mt-0.5">
            Order: <span className="font-semibold text-sap-blue">{order.order_no}</span> — {order.item_no}
            <span className="text-sap-textLight"> · Internal ID {order.prod_order_id}</span>
            {order.component_count != null && (
              <span className="text-sap-textLight"> · {Number(order.component_count) || 0} component row(s) on order</span>
            )}
          </p>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gradient-to-r from-[#2E3A4D] to-[#3D4B5F] sticky top-0">
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" checked={allChecked} onChange={e => toggleAll(e.target.checked)}
                    className="accent-sap-blue" />
                </th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">#</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">Order Number</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">Row No.</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">Item Number</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">Item Description</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">Type</th>
                <th className="text-right px-3 py-2 font-semibold text-sap-textMuted text-2xs">Qty</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">Whse</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">Start Date</th>
                <th className="text-left px-3 py-2 font-semibold text-sap-textMuted text-2xs">End Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-sap-textLight italic">Loading components…</td></tr>
              ) : comps.length === 0 ? (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-sap-textLight italic">No components found.</td></tr>
              ) : comps.map((c, i) => (
                <tr key={`${c.line_type || 'row'}-${c.comp_id ?? 'n'}-${i}`}
                  onClick={() => setChecked(prev => ({ ...prev, [i]: !prev[i] }))}
                  className={`cursor-pointer ${checked[i] ? 'bg-sap-blue/10' : i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'}
                    border-b border-sap-border/40 hover:bg-sap-blue/5`}>
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={!!checked[i]} readOnly className="accent-sap-blue pointer-events-none" />
                  </td>
                  <td className="px-3 py-2 text-sap-textLight">{i + 1}</td>
                  <td className="px-3 py-2 font-semibold text-sap-blue flex items-center gap-1">
                    <svg width="10" height="10" viewBox="0 0 14 14" className="drop-shadow-sm flex-shrink-0">
                      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                    </svg>
                    {order.order_no}
                  </td>
                  <td className="px-3 py-2">{c.comp_id || i + 1}</td>
                  <td className="px-3 py-2 font-semibold flex items-center gap-1">
                    <svg width="10" height="10" viewBox="0 0 14 14" className="drop-shadow-sm flex-shrink-0">
                      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                    </svg>
                    {c.item_no || c.resource_no || '—'}
                  </td>
                  <td className="px-3 py-2">{c.item_desc || ''}</td>
                  <td className="px-3 py-2">{c.line_type || 'Item'}</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {Number(Math.max(0, Number(c.planned_qty) - Number(c.issued_qty))).toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-sap-textLight">{c.wh_code || '—'}</td>
                  <td className="px-3 py-2 text-sap-textLight">{fmtDate(c.start_date)}</td>
                  <td className="px-3 py-2 text-sap-textLight">{fmtDate(c.end_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-sap-border flex justify-between items-center">
          <span className="text-xs text-sap-textLight">
            {selectedComps.length} of {comps.length} selected
            {selectedComps.some(c => Math.max(0, Number(c.planned_qty) - Number(c.issued_qty)) <= 0) && (
              <span className="block mt-1 text-amber-700">Some selected rows have 0 open quantity — they will not post until you increase quantity on the issue.</span>
            )}
          </span>
          <div className="flex gap-2">
            <button onClick={() => onConfirm(order, selectedComps)} disabled={selectedComps.length === 0}
              className="px-5 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-40">
              OK
            </button>
            <button onClick={onClose}
              className="px-5 py-1.5 text-xs font-semibold border border-sap-border rounded hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function IssueForProductionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const blankGeneral = {
    ok_for_quality: '', ok_for_dispatch: '', document_type: '', u_base_entry: '',
    courier_name: '', docket_no: '', supplier_code: '', courier_details: '',
    expected_delivery_date: '', pod: '', gr_no: '', guarantee: '', bg_validity_date: '',
    guarantee_amount: '0.00', e_way_bill: '', e_way_bill_no: '', gstr_2b: 'Yes',
    original_doc_no: '', service_po_no: '', customer_po_details: '',
    gin_number: '', project_code: '', rework_value: '', doc_total: '0.00',
    gate_entry_no: '', irn_number: '', acknowledgement_number: '',
  };

  const [form, setForm] = useState({
    issue_number: '', posting_date: new Date().toISOString().slice(0, 10),
    remarks: '', journal_remark: 'Issue for Production',
    prod_order_id: '', order_no: '', item_no: '', product_desc: '',
    status: 'Posted', total_amount: 0,
    series: 'Primary', ref2: '', branch: '',
  });
  const [general, setGeneral] = useState({ ...blankGeneral });
  const [showGeneral, setShowGeneral] = useState(true);
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [lines, setLines] = useState([]);
  const [issueSeriesList, setIssueSeriesList] = useState([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const [tab, setTab] = useState('contents');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [orderPicker, setOrderPicker] = useState(false);
  const [copyDialog, setCopyDialog] = useState({ open: false, order: null });
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, name: '' });
  const pdfBlobUrlRef = useRef(null);
  const [pendingSubRows, setPendingSubRows] = useState([]);
  const [subcontractingSubId, setSubcontractingSubId] = useState('');
  const [linkedSubSummary, setLinkedSubSummary] = useState('');
  const [prodOrderBudget, setProdOrderBudget] = useState(0);

  const loadIssue = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const data = await api.get(`/production-orders/issues/${id}`);
      setForm({
        issue_number: data.issue_number || '',
        posting_date: data.posting_date ? String(data.posting_date).slice(0, 10) : '',
        remarks: data.remarks || '',
        journal_remark: 'Issue for Production',
        prod_order_id: data.prod_order_id,
        order_no: data.order_no || '',
        item_no: data.item_no || '',
        product_desc: data.product_desc || '',
        status: data.status || 'Posted',
        total_amount: Number(data.total_amount) || 0,
      });
      const g = { ...blankGeneral };
      for (const k of Object.keys(blankGeneral)) {
        if (data[k] !== undefined && data[k] !== null) g[k] = String(data[k]);
      }
      if (g.expected_delivery_date) g.expected_delivery_date = String(g.expected_delivery_date).slice(0, 10);
      if (g.bg_validity_date) g.bg_validity_date = String(g.bg_validity_date).slice(0, 10);
      // Project code on issue follows production order (same code as SO/PO flow) if not set on issue
      if (!String(g.project_code || '').trim() && data.project_no) {
        g.project_code = String(data.project_no);
      }
      setGeneral(g);
      setLines((data.lines || []).map(l => ({
        ...l,
        item_no: l.item_no || '',
        item_desc: l.item_desc || l.description || '',
        wh_code: l.wh_code || '',
        in_stock: Number(l.in_stock) || 0,
        planned_qty: Number(l.planned_qty) || 0,
        issued_qty: Number(l.issued_qty) || 0,
      })));
      const sid = data.subcontracting_sub_id;
      if (sid && data.prod_order_id) {
        try {
          const ord = await api.get(`/production-orders/${data.prod_order_id}`);
          const line = (ord.subcontracting || []).find(s => Number(s.sub_id) === Number(sid));
          if (line) {
            const op = (line.operation || '').trim() || '—';
            const vn = line.vendor_code || '—';
            const ch = line.challan_no ? ` · ${line.challan_no}` : '';
            setLinkedSubSummary(`${op} — ${vn} · ${line.status || ''}${ch}`);
          } else {
            setLinkedSubSummary(`Subcontracting line #${sid}`);
          }
        } catch {
          setLinkedSubSummary(`Subcontracting line #${sid}`);
        }
      } else {
        setLinkedSubSummary('');
      }
    } catch (e) { setError(e.message || 'Failed to load issue'); }
    finally { setLoading(false); }
  }, [id, isNew]);

  /* load series */
  useEffect(() => {
    api.get('/production-orders/issues/series')
      .then(list => {
        setIssueSeriesList(list || []);
        if (isNew && list?.length) {
          const def = list.find(s => s.is_default) || list[0];
          setSelectedSeriesId(def.series_id);
          setForm(f => ({ ...f, series: def.series_name, series_id: def.series_id }));
          api.get(`/production-orders/issues/next-number?series_id=${def.series_id}`)
            .then(r => setForm(f => ({ ...f, issue_number: r?.next || '' })))
            .catch(() => {});
        }
      })
      .catch(() => {});
  }, [isNew]);

  /* handle series change */
  const handleSeriesChange = (seriesId) => {
    const sid = parseInt(seriesId, 10);
    const chosen = issueSeriesList.find(s => s.series_id === sid);
    if (!chosen) return;
    setSelectedSeriesId(sid);
    setForm(f => ({ ...f, series: chosen.series_name, series_id: sid }));
    if (isNew) {
      api.get(`/production-orders/issues/next-number?series_id=${sid}`)
        .then(r => setForm(f => ({ ...f, issue_number: r?.next || '' })))
        .catch(() => {});
    }
  };

  useEffect(() => { loadIssue(); }, [loadIssue]);

  useEffect(() => {
    setSubcontractingSubId('');
  }, [form.prod_order_id]);

  useEffect(() => {
    if (!isNew || !form.prod_order_id) {
      setPendingSubRows([]);
      return;
    }
    let cancelled = false;
    api.get(`/production-orders/${form.prod_order_id}`)
      .then((d) => {
        if (cancelled) return;
        setPendingSubRows((d.subcontracting || []).filter((s) => s.status === 'Pending'));
      })
      .catch(() => { if (!cancelled) setPendingSubRows([]); });
    return () => { cancelled = true; };
  }, [isNew, form.prod_order_id]);

  const pickOrder = (order) => {
    setOrderPicker(false);
    setCopyDialog({ open: true, order });
  };

  const confirmCopy = (order, selectedComps) => {
    setCopyDialog({ open: false, order: null });
    setForm(f => ({
      ...f,
      prod_order_id: order.prod_order_id,
      order_no: order.order_no,
      item_no: order.item_no,
      product_desc: order.description || '',
    }));

    if (order.prod_order_id) {
      api.get(`/production-orders/${order.prod_order_id}`)
        .then(ord => {
          const budget = (ord.components || selectedComps || []).reduce((acc, c) => acc + (Number(c.planned_qty || 0) * Number(c.unit_cost || 0)), 0) || Number(ord.total_cost) || 0;
          setProdOrderBudget(budget);
        })
        .catch(() => {
          const fallbackBudget = (selectedComps || []).reduce((acc, c) => acc + (Number(c.planned_qty || 0) * Number(c.unit_cost || 0)), 0);
          setProdOrderBudget(fallbackBudget);
        });
    }

    setGeneral(g => ({
      ...g,
      project_code: (order.project_no != null && String(order.project_no).trim() !== '')
        ? String(order.project_no).trim()
        : g.project_code,
    }));
    setLines(selectedComps.map((c, idx) => ({
      _row: idx + 1,
      line_type: c.line_type,
      item_id: c.item_id,
      item_no: c.item_no || c.resource_no || '',
      item_desc: c.item_desc || '',
      quantity: Math.max(0, Number(c.planned_qty) - Number(c.issued_qty)),
      unit_cost: Number(c.unit_cost) || 0,
      total_cost: Math.max(0, Number(c.planned_qty) - Number(c.issued_qty)) * (Number(c.unit_cost) || 0),
      warehouse_id: c.warehouse_id,
      wh_code: c.wh_code || '',
      in_stock: Number(c.in_stock) || 0,
      planned_qty: Number(c.planned_qty) || 0,
      issued_qty: Number(c.issued_qty) || 0,
      resource_id: c.resource_id,
      comp_id: c.comp_id,
    })));
  };

  const updateLineQty = (idx, val) => {
    setLines(prev => prev.map((l, i) => {
      if (i !== idx) return l;
      const qty = Number(val) || 0;
      return { ...l, quantity: qty, total_cost: qty * (Number(l.unit_cost) || 0) };
    }));
  };

  const totalAmount = lines.reduce((sum, l) => sum + (Number(l.total_cost) || 0), 0);
  const isOverBudget = prodOrderBudget > 0 && totalAmount > (prodOrderBudget + 0.01);

  const handleSave = async () => {
    if (!form.prod_order_id) return setError('Please select a Production Order');
    const issuableLines = lines.filter(l => Number(l.quantity) > 0);
    if (issuableLines.length === 0) return setError('At least one line with quantity > 0 is required');
    if (isOverBudget) {
      return setError('ISSUE FOR PRODUCTION TOTAL SHOULD NOT BE GREATER THAN BUDGET');
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        posting_date: form.posting_date,
        remarks: form.remarks || null,
        ...general,
        lines: issuableLines.map(l => ({
          line_type: l.line_type || 'Item',
          comp_id: l.comp_id != null && l.comp_id !== '' ? l.comp_id : null,
          item_id: l.item_id || null,
          resource_id: l.resource_id || null,
          description: l.item_desc || '',
          quantity: Number(l.quantity),
          unit_cost: Number(l.unit_cost) || 0,
          warehouse_id: l.warehouse_id || null,
        })),
      };
      const subLink = parseInt(String(subcontractingSubId || '').trim(), 10);
      if (Number.isFinite(subLink) && subLink > 0) {
        payload.subcontracting_sub_id = subLink;
      }
      if (isNew && selectedSeriesId) {
        const consumed = await api.post('/production-orders/issues/consume-number', { series_id: selectedSeriesId });
        if (consumed?.consumed) {
          payload.issue_number = consumed.consumed;
          payload.series_id = selectedSeriesId;
          payload.series = consumed.series_name;
        }
      }
      const result = await api.post(`/production-orders/${form.prod_order_id}/issue`, payload);
      navigate(`/production/issue/${result.issue_id}`, { replace: true });
    } catch (e) { setError(e.message || 'Failed to create issue'); }
    finally { setSaving(false); }
  };

  const saveGeneral = async () => {
    if (isNew) return;
    setSavingGeneral(true);
    try {
      await api.put(`/production-orders/issues/${id}/general`, general);
    } catch (e) { setError(e.message || 'Failed to save general fields'); }
    finally { setSavingGeneral(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this Goods Issue? This will also revert the issued quantities on the components and restore warehouse stock levels.')) return;

    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      await api.delete(`/production-orders/issues/${id}`);
      setSuccessMsg('Goods Issue permanently deleted');
      setTimeout(() => navigate('/production/issues'), 1000);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to delete issue');
      setSaving(false);
    }
  };

  const setGen = (k, v) => setGeneral(g => ({ ...g, [k]: v }));

  const closePdfModal = () => {
    setPdfModal((m) => {
      if (m.url) {
        URL.revokeObjectURL(m.url);
        pdfBlobUrlRef.current = null;
      }
      return { open: false, url: null, name: '' };
    });
  };

  useEffect(() => {
    return () => {
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
    };
  }, []);

  const openPdfPreview = async () => {
    if (isNew) return;
    setPdfLoading(true);
    setError('');
    try {
      const base = getApiBase();
      const token = getToken();
      const qs = new URLSearchParams({ _ts: String(Date.now()) });
      const res = await fetch(
        `${base}/production-orders/issues/${encodeURIComponent(id)}/challan-pdf?${qs}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          cache: 'no-store',
        }
      );
      if (!res.ok) {
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        if (ct.includes('application/json')) {
          const j = await res.json();
          throw new Error(j.message || `PDF failed (${res.status})`);
        }
        throw new Error(`PDF failed (${res.status})`);
      }
      const buf = await res.arrayBuffer();
      const pdfBlob = new Blob([buf], { type: 'application/pdf' });
      const name = `Delivery_Challan_${(form.issue_number || id).replace(/[^\w.-]+/g, '_')}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      if (pdfBlobUrlRef.current) URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = url;
      setPdfModal({ open: true, url, name });
    } catch (e) {
      setError(e.message || 'Failed to open PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadChallanFile = () => {
    if (!pdfModal.url) return;
    const a = document.createElement('a');
    a.href = pdfModal.url;
    a.download = pdfModal.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const exportChallanPrint = () => {
    if (!pdfModal.url) return;
    const w = window.open(pdfModal.url, '_blank');
    if (w) w.addEventListener('load', () => { try { w.print(); } catch { /* ignore */ } });
  };

  const fmtAmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-sap-textLight text-sm">Loading issue…</div>;
  }

  return (
    <div className="flex gap-4">
    <div className={`space-y-4 ${showGeneral ? 'flex-1 min-w-0' : 'w-full'}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/production/issues')}
            className="p-1.5 hover:bg-white rounded-lg transition-colors">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <div>
            <h1 className="text-base font-bold text-sap-text">
              {isNew ? 'Issue for Production – Add' : `Issue for Production – ${form.issue_number}`}
            </h1>
            {!isNew && (
              <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-2xs font-semibold
                ${form.status === 'Posted' ? 'bg-[#E8F4FE] text-[#2184F2]' : 'bg-[#F0F4F3] text-[#6B7F7F]'}`}>
                {form.status}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button type="button" onClick={openPdfPreview} disabled={pdfLoading}
              className="px-3 py-1.5 text-xs font-semibold bg-sap-blue text-white border border-sap-blue rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
              title="Preview challan, then download or print">
              {pdfLoading ? 'PDF…' : 'PDF'}
            </button>
          )}
          {!isNew && (
            <button type="button" onClick={handleDelete} disabled={saving}
              className="px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors">
              {saving ? 'Deleting…' : 'Delete'}
            </button>
          )}
          {!showGeneral && (
            <button type="button" onClick={() => setShowGeneral(true)}
              className="px-3 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-white transition-colors flex items-center gap-1">
              General ▸
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-4 py-2 rounded-lg">{error}</div>
      )}
      {isOverBudget && (
        <div className="bg-red-600 text-white font-bold text-xs px-4 py-2.5 rounded-lg border border-red-700 shadow-md flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span className="tracking-wide uppercase">ISSUE FOR PRODUCTION TOTAL SHOULD NOT BE GREATER THAN BUDGET</span>
          </div>
          <div className="text-right font-mono text-2xs bg-black/20 px-2 py-1 rounded">
            <div>Issue Total: ₹ {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
            <div>Order Budget: ₹ {prodOrderBudget.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
          </div>
        </div>
      )}
      {successMsg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs px-4 py-2 rounded-lg">{successMsg}</div>
      )}

      {/* Form header */}
      <div className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* Number */}
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Number</label>
            <input value={form.issue_number || '(Auto)'} readOnly className={`w-full ${inp} bg-gray-50 text-sap-textLight`} />
          </div>
          {/* Series */}
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Series</label>
            {isNew ? (
              <div className="relative">
                <select
                  value={selectedSeriesId || ''}
                  onChange={e => handleSeriesChange(e.target.value)}
                  className="w-full h-8 pl-3 pr-8 text-xs border border-sap-border rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/20 focus:border-sap-blue appearance-none">
                  {issueSeriesList.map(s => (
                    <option key={s.series_id} value={s.series_id}>{s.series_name}</option>
                  ))}
                </select>
                <svg className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-sap-textLight" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </div>
            ) : (
              <input value={form.series || 'Primary'} readOnly className={`w-full ${inp} bg-gray-50 text-sap-textLight`} />
            )}
          </div>
          {/* Posting Date */}
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Posting Date</label>
            <input type="date" value={form.posting_date}
              disabled={!isNew}
              onChange={e => setForm({ ...form, posting_date: e.target.value })}
              className={`w-full ${inp} ${!isNew ? 'bg-gray-50' : ''}`} />
          </div>
          {/* Ref. 2 */}
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Ref. 2</label>
            <input value={form.ref2 || ''}
              disabled={!isNew}
              onChange={e => setForm({ ...form, ref2: e.target.value })}
              placeholder=""
              className={`w-full ${inp} ${!isNew ? 'bg-gray-50' : ''}`} />
          </div>
          {/* Branch */}
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Branch</label>
            <input value={form.branch || ''} readOnly className={`w-full ${inp} bg-gray-50`} />
          </div>
        </div>
        {isNew && form.prod_order_id ? (
          <div className="pt-3 border-t border-sap-border/50">
            <label className="text-xs text-sap-textLight mb-1 block">Link to subcontracting line (optional)</label>
            <select
              value={subcontractingSubId}
              onChange={(e) => setSubcontractingSubId(e.target.value)}
              className={`w-full max-w-xl ${inp}`}
            >
              <option value="">— None —</option>
              {pendingSubRows.map((s) => (
                <option key={s.sub_id} value={String(s.sub_id)}>
                  {(String(s.operation || '').trim() || '—')} — {s.vendor_code || 'No vendor'} · Send qty {s.send_qty ?? 0}
                </option>
              ))}
            </select>
            {pendingSubRows.length === 0 ? (
              <p className="text-2xs text-sap-textLight mt-1 italic">No pending subcontracting lines on this production order.</p>
            ) : null}
          </div>
        ) : null}
        {!isNew && linkedSubSummary ? (
          <div className="pt-3 border-t border-sap-border/50">
            <label className="text-xs text-sap-textLight mb-1 block">Subcontracting link</label>
            <div className="text-xs text-sap-text font-medium">{linkedSubSummary}</div>
          </div>
        ) : null}
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="flex border-b border-sap-border">
          {['contents', 'attachments'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-2.5 text-xs font-semibold transition-colors capitalize
                ${tab === t ? 'text-sap-blue border-b-2 border-sap-blue bg-sap-blue/5' : 'text-sap-textLight hover:text-sap-text'}`}>
              {t === 'contents' ? `Contents (${lines.length})` : 'Attachments'}
            </button>
          ))}
        </div>

        {tab === 'contents' && (
          <div className="overflow-x-auto">
            {isNew && lines.some(l => Number(l.quantity) <= 0) && (
              <div className="mx-3 mt-3 mb-1 px-3 py-2 text-2xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md">
                Rows with quantity 0 are shown for reference but are not saved when you post this issue — only lines with quantity greater than 0 are included.
              </div>
            )}
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className={`${thCls} w-10`}>#</th>
                  <th className={`${thCls} w-28`}>Order No.</th>
                  <th className={`${thCls} w-16`}>Row No.</th>
                  <th className={`${thCls} w-16`}>Type</th>
                  <th className={`${thCls} w-32`}>Item No.</th>
                  <th className={`${thCls} w-40`}>Item Description</th>
                  <th className={`${thCls} w-24 text-right`}>Quantity</th>
                  <th className={`${thCls} w-20`}>Whse</th>
                  <th className={`${thCls} w-24 text-right`}>In Stock</th>
                  <th className={`${thCls} w-24 text-right`}>Item Cost</th>
                  <th className={`${thCls} w-24 text-right`}>Planned</th>
                  <th className={`${thCls} w-24 text-right`}>Issued</th>
                  <th className={`${thCls} w-24 text-right`}>Avail</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr><td colSpan={13} className="px-3 py-8 text-center text-sap-textLight italic">
                    {isNew ? 'Select a production order to load components.' : 'No lines.'}
                  </td></tr>
                ) : lines.map((l, i) => (
                  <tr key={l.line_id || `${l.line_type || 'Item'}-${l.comp_id ?? i}-${i}`}
                    className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'} border-b border-sap-border/40`}>
                    <td className="px-3 py-2 text-sap-textLight">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <button type="button" title="Open Production Order"
                          onClick={(e) => { e.stopPropagation(); if (form.prod_order_id) navigate(`/production-order/${form.prod_order_id}`); }}
                          className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80">
                          <svg width="10" height="10" viewBox="0 0 14 14" className="drop-shadow-sm">
                            <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                          </svg>
                        </button>
                        <span className="font-semibold text-sap-blue">{form.order_no}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">{l.comp_id || i + 1}</td>
                    <td className="px-3 py-2">{l.line_type || 'Item'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {l.line_type === 'Item' && l.item_no && (
                          <button type="button" title={`Open Item Master for ${l.item_no}`}
                            onClick={(e) => { e.stopPropagation(); navigate(`/item-master/${encodeURIComponent(l.item_no)}`); }}
                            className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80">
                            <svg width="10" height="10" viewBox="0 0 14 14" className="drop-shadow-sm">
                              <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                            </svg>
                          </button>
                        )}
                        <span className="font-semibold">
                          {l.line_type === 'Resource' ? (l.resource_no || l.item_no || '—') : (l.item_no || '—')}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2">{l.line_type === 'Resource' ? (l.resource_desc || l.item_desc || '') : l.item_desc}</td>
                    <td className="px-3 py-2 text-right">
                      {isNew ? (
                        <input type="number" min="0" step="any"
                          value={l.quantity}
                          onChange={e => updateLineQty(i, e.target.value)}
                          className="w-20 h-7 px-1 text-xs text-right border border-sap-border rounded focus:outline-none focus:ring-1 focus:ring-sap-blue/40" />
                      ) : (
                        <span className="font-semibold">{Number(l.quantity)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        {l.warehouse_id && (
                          <button type="button" title={`Open Warehouse ${l.wh_code}`}
                            onClick={(e) => { e.stopPropagation(); navigate('/admin/setup/warehouses'); }}
                            className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80">
                            <svg width="10" height="10" viewBox="0 0 14 14" className="drop-shadow-sm">
                              <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                            </svg>
                          </button>
                        )}
                        <span className="text-sap-textLight">{l.wh_code || '—'}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">{Number(l.in_stock || 0)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtAmt(l.unit_cost)}</td>
                    <td className="px-3 py-2 text-right">{Number(l.planned_qty || 0)}</td>
                    <td className="px-3 py-2 text-right">{Number(l.issued_qty || 0)}</td>
                    <td className="px-3 py-2 text-right">{Math.max(0, Number(l.planned_qty || 0) - Number(l.issued_qty || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'attachments' && (
          <div className="px-6 py-10 text-center text-xs text-sap-textLight italic">
            No attachments.
          </div>
        )}
      </div>

      {/* Footer: Remarks + Actions */}
      <div className="bg-white rounded-2xl shadow-sm p-5">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Remarks</label>
            <textarea value={form.remarks}
              disabled={!isNew}
              onChange={e => setForm({ ...form, remarks: e.target.value })}
              rows={2}
              className={`w-full px-2 py-1.5 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40 resize-none ${!isNew ? 'bg-gray-50' : ''}`} />
          </div>
          <div>
            <label className="text-xs text-sap-textLight mb-1 block">Journal Remark</label>
            <div className="flex items-center gap-1">
              <input value={form.journal_remark} readOnly className={`flex-1 ${inp} bg-gray-50`} />
              {form.prod_order_id && (
                <button
                  type="button"
                  title="Open Production Order"
                  onClick={() => navigate(`/production-order/${form.prod_order_id}`)}
                  className="flex-shrink-0 flex items-center justify-center w-6 h-6 hover:opacity-80 transition-opacity">
                  <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
                    <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {isNew && (
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-sap-border">
            <button onClick={handleSave} disabled={saving}
              className="px-5 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark
                         shadow-sm transition-all disabled:opacity-50">
              {saving ? 'Posting…' : 'Add'}
            </button>
            <button onClick={() => navigate('/production/issues')}
              className="px-5 py-2 text-xs font-semibold border border-sap-border rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        )}

        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-sap-border">
          <button onClick={() => {
              if (isNew) setOrderPicker(true);
              else if (form.prod_order_id) navigate(`/production-order/${form.prod_order_id}`);
              else navigate('/production/orders');
            }}
            className="px-5 py-2 text-xs font-semibold bg-[#5A6F7F] text-white rounded-lg hover:bg-[#4A5F6F] shadow-sm transition-all">
            Production Order
          </button>
          <button onClick={() => navigate('/production/rework')}
            className="px-5 py-2 text-xs font-semibold bg-[#5A6F7F] text-white rounded-lg hover:bg-[#4A5F6F] shadow-sm transition-all">
            Disassembly Order
          </button>
        </div>
      </div>

      {/* Order Picker Dialog (step 1) */}
      <OrderPickerDialog open={orderPicker} onClose={() => setOrderPicker(false)} onSelect={pickOrder} />

      {/* Select Items to Copy Dialog (step 2) */}
      <SelectItemsToCopyDialog
        open={copyDialog.open}
        order={copyDialog.order}
        onClose={() => setCopyDialog({ open: false, order: null })}
        onConfirm={confirmCopy}
      />
    </div>

    {/* ═══ General Side Panel ═══ */}
    {showGeneral && (
      <div className="w-[280px] flex-shrink-0 space-y-0">
        <div className="card">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-sap-border">
            <div className="flex items-center gap-1">
              <button onClick={() => setShowGeneral(false)} className="text-sap-textMuted hover:text-sap-text text-xs">◂</button>
              <span className="text-xs font-semibold text-sap-textMuted">General</span>
            </div>
            {!isNew && (
              <button onClick={saveGeneral} disabled={savingGeneral}
                className="text-2xs px-2 py-0.5 bg-white/20 text-white rounded hover:bg-white/30 disabled:opacity-50">
                {savingGeneral ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
          <div className="px-3 py-2 space-y-1.5 max-h-[calc(100vh-120px)] overflow-y-auto">
            {[
              ['OK for Quality',        'ok_for_quality',         'select', ['', 'Yes', 'No']],
              ['OK for Dispatch',       'ok_for_dispatch',        'select', ['', 'Yes', 'No']],
              ['Document Type',         'document_type',          'select', ['', 'Standard', 'Return', 'Transfer']],
              ['U_BaseEntry',           'u_base_entry',           'text'],
              ['Courier Name',          'courier_name',           'text'],
              ['Docket No',             'docket_no',              'text'],
              ['Supplier Code',         'supplier_code',          'text'],
              ['Courier Details',       'courier_details',        'text'],
              ['Expected Delivery Date','expected_delivery_date', 'date'],
              ['POD',                   'pod',                    'text'],
              ['GR No',                 'gr_no',                  'text'],
              ['Guarantee',             'guarantee',              'text'],
              ['BG Validity Date',      'bg_validity_date',       'date'],
              ['Guarantee Amount',      'guarantee_amount',       'number'],
              ['E Way Bill',            'e_way_bill',             'select', ['', 'Yes', 'No']],
              ['E Way Bill No',         'e_way_bill_no',          'text'],
              ['GSTR 2B',               'gstr_2b',                'select', ['Yes', 'No']],
              ['Original Document No.', 'original_doc_no',        'text'],
              ['Service PO No',         'service_po_no',          'text'],
              ['Customer PO Details',   'customer_po_details',    'text'],
              ['DocTotal',              'doc_total',              'number'],
              ['GIN Number',            'gin_number',             'text'],
              ['Project Code',          'project_code',           'text'],
              ['REWORK VALUE',          'rework_value',           'text'],
              ['Gate Entry No.',        'gate_entry_no',          'text'],
              ['IRN Number',            'irn_number',             'text'],
              ['Acknowledgement Number','acknowledgement_number', 'text'],
            ].map(([label, key, type, opts]) => (
              <div key={key} className="flex items-center gap-1">
                <label className="text-[10px] text-sap-textLight w-[110px] flex-shrink-0 leading-tight">{label}</label>
                {type === 'select' ? (
                  <select value={general[key] || ''} onChange={e => setGen(key, e.target.value)}
                    className="flex-1 h-6 px-1 text-[11px] border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40">
                    {(opts || []).map(o => <option key={o} value={o}>{o || '—'}</option>)}
                  </select>
                ) : (
                  <input type={type || 'text'} value={general[key] || ''}
                    onChange={e => setGen(key, e.target.value)}
                    className="flex-1 h-6 px-1 text-[11px] border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    )}

    {pdfModal.open && pdfModal.url && (
      <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-sap-border">
            <h3 className="text-sm font-bold text-sap-text">Delivery Challan — Preview</h3>
            <button type="button" onClick={closePdfModal} className="text-sap-textLight hover:text-red-600 text-lg leading-none px-1" aria-label="Close">
              &times;
            </button>
          </div>
          {/* object/embed: more reliable than iframe for blob: PDFs in production (Chrome/Edge); iframe can trigger download */}
          <div className="w-full flex-1 min-h-[min(70vh,640px)] bg-gray-100">
            <object
              data={pdfModal.url}
              type="application/pdf"
              title="Delivery Challan PDF"
              className="w-full h-full min-h-[min(70vh,640px)] border-0">
              <embed src={pdfModal.url} type="application/pdf" className="w-full min-h-[min(70vh,640px)]" />
            </object>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 px-4 py-3 border-t border-sap-border bg-[#F8FAFA]">
            <button type="button" onClick={downloadChallanFile}
              className="px-4 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark">
              Download
            </button>
            <button type="button" onClick={exportChallanPrint}
              className="px-4 py-2 text-xs font-semibold border border-sap-border rounded-lg hover:bg-white bg-white" title="Opens print — save as PDF from the print dialog">
              Export
            </button>
            <button type="button" onClick={closePdfModal}
              className="px-4 py-2 text-xs font-semibold text-sap-text border border-sap-border rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    )}
    </div>
  );
}
